/**
 * game-hanga.js — cutting a block, and pulling a print off it.
 *
 * TWO STAGES AND THREE DRAGS, and every one of them is a real thing a printer
 * did rather than a button:
 *
 *   CARVING   drag the knife down each side of the line, then clear the waste
 *             between. Straying off the line takes a nick out of it, and the
 *             nick stays in the print for as long as she owns it.
 *
 *   WIPING    on a bokashi board, how far you drag the brush decides how far
 *             the colour reaches. It is the only board where inking is not a
 *             formality, and the Plum Estate has two of them pulling opposite
 *             ways.
 *
 *   SEATING   drag the sheet until its corner drops into the kentō. Inside
 *             the notch it clicks home dead square; outside it, the board
 *             prints exactly where you put it, and the whole print is wrong
 *             in a way you can see from across the room.
 *
 *   RUBBING   drag the baren over the back of the sheet. Ink takes where you
 *             rubbed and nowhere else.
 *
 * WHAT THIS FILE DOES NOT DECIDE is what any of it was worth. The recipe goes
 * to the worker and the worker judges it; the numbers shown here come back
 * from that. A second opinion about the score in this file would be a second
 * thing to be wrong, and it would be the one a modified page could edit.
 *
 * EVERYTHING IS IN MILLIMETRES OF A REAL SHEET, not in pixels. An ōban sheet
 * is 255 by 370, the tolerances in hanga-config are in those units, and the
 * page converts at the edges — so the game is exactly as hard on a phone as
 * it is on a laptop, which a pixel tolerance would not be.
 */
(function () {
  var Art = window.HangaArt;
  var root = document.querySelector('[data-hanga-game]');
  if (!root || !Art) return;

  var BASE = '/apps/arcade';
  var S = {
    data: null,        // the worker's last word about her shelf
    view: 'shelf',     // shelf | carve | press | done
    print: null,       // which print, as a key
    busy: false,
    said: null,        // { text, kind }
    carve: null,
    press: null,
    done: null,
  };

  async function api(path, body) {
    var res = await fetch(BASE + path, {
      method: body ? 'POST' : 'GET',
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
    var data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok) {
      var err = new Error((data && data.message) || 'That did not work.');
      /* `error`, NOT `code` — the worker serialises every refusal as
         { error: <code>, message }, and reading `code` is how a whole branch
         of game-garden.js's error handling was dead for a month. */
      err.code = (data && data.error) || ('http_' + res.status);
      throw err;
    }
    return data;
  }

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  };
  var money = function (n) { return Number(n || 0).toLocaleString('en-US'); };

  /* NEVER SHOW A CHILD A CODE. HttpError does `super(message || code)`, so a
     refusal thrown without a sentence arrives with its message equal to its
     code — which is how a guest was once shown the word "login_required". */
  var HUMAN = {
    login_required: 'Sign in to print — your sheets and your prints are kept with your account.',
    not_enough_points: 'You have not got enough for the paper and the ink yet.',
    no_sheet: 'There is no sheet on the press. Take one first.',
    sheet_open: 'There is already a sheet on the press.',
    no_such_print: 'That is not one of the prints.',
    not_found: 'The workshop is not open yet.',
    server_error: 'Something went wrong at our end. Try again in a moment.',
  };
  function human(err) {
    return (err && err.message && err.message !== err.code && err.message)
      || HUMAN[err && err.code] || 'That did not work.';
  }

  /* ---- the print, and what a board is -------------------------------- */

  function printOf(key) { return Art.PRINTS[key] || null; }
  function defOf(key) {
    var list = (S.data && S.data.prints) || [];
    for (var i = 0; i < list.length; i++) if (list[i].key === key) return list[i];
    return null;
  }
  /* The boards a player actually pulls: the sheet is the paper, not a board. */
  function boardsOf(key) {
    var p = printOf(key);
    if (!p) return [];
    return Art.boardsOf(p).filter(function (b) { return !b.sheet; });
  }

  /**
   * THE LAB'S BRUSH, NOT A SECOND ONE.
   *
   * This file used to draw blocks itself, and being a second copy of
   * hanga-lab's printSvg it was wrong in three ways nobody could see from
   * here: it dropped the clip paths, so Red Fuji's trees stippled across the
   * sky; it filled the blocks that are meant to be STROKED, so Hokusai's
   * fishing lines and water ribs vanished; and it knew nothing about the
   * bokashi gradients, so every wiped board came out blank — the one idea
   * this game exists to teach, missing from the game.
   *
   * emit-hanga-art.mjs bundles the lab's own `blockSvg` and `defsSvg` for
   * exactly this reason. There is one brush now.
   */
  function dJoin(d) { return Art.dOf ? Art.dOf(d) : (Array.isArray(d) ? d.join(' ') : d); }

  /**
   * WHICH BOARD SHE CARVES. The key block where there is one; where there is
   * not — Red Fuji has almost no line in it at all — the finest colour board
   * instead, which is the one with the most separate pieces on it.
   *
   * AND THE GAME SAYS WHY rather than quietly picking one, because "this
   * print has no black line, so you are cutting the snow" is the single most
   * interesting thing about Red Fuji and it would be a shame to hide it
   * behind a sensible default.
   */
  /* ---- millimetres, and the one place pixels are allowed -------------- */

  var SHEET = (S.data && S.data.sheet) || { w: 255, h: 370 };
  /* A print's drawing is in its own units; the sheet is in millimetres. This
     is the only conversion in the file and everything else stays in mm. */
  function perMm(key) { var p = printOf(key); return p ? p.w / SHEET.w : 1; }

  /* ================================================================== *
   * STAGE ONE — CARVING
   *
   * The guide is the board's own path, sampled into points by the browser's
   * own geometry rather than by anything this file works out: a hidden
   * <path> in a scratch <svg>, getTotalLength, getPointAtLength. Re-deriving
   * bezier arithmetic here would be a second opinion about where the line is,
   * and the print is drawn from the first one.
   * ================================================================== */

  var scratch = null;
  function samplePath(d, tol, w, h, edge) {
    if (!scratch) {
      scratch = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      scratch.setAttribute('width', '0');
      scratch.setAttribute('height', '0');
      scratch.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
      document.body.appendChild(scratch);
    }
    var el = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    el.setAttribute('d', d);
    scratch.appendChild(el);
    var out = [];
    try {
      var len = el.getTotalLength();
      /* HOW FINELY, AND WHY IT IS THE TOLERANCE THAT DECIDES.
       *
       * These points stand in for the line, and the knife is measured
       * against the straight segments between them. A straight segment cuts
       * the corner of a curve, so on a tight twig a stroke sitting exactly
       * on the drawn line reads as a fraction outside it — and with the old
       * spacing of one sample every three units that fraction was bigger
       * than the tolerance itself. A perfect trace of the Plum Estate's tree
       * scored three nicks it had not earned.
       *
       * Half a tolerance keeps that error near a twentieth of a unit, which
       * is far below anything the game judges by. The cap is still there for
       * a print with metres of line in it, but it is no longer what decides
       * the spacing on any of the five. */
      var step = Math.max(tol / 2, len / 3000);
      for (var at = 0; at <= len; at += step) {
        var pt = el.getPointAtLength(at);
        /* ONLY WHAT IS ON THE FACE OF THE PLANK.
         *
         * Two reasons, and the second one took a while to see.
         *
         * A drawing can run PAST the edge of the print — the Plum Estate's
         * tree does, by thirty units off the bottom — because the finished
         * picture clips it. The guide does not, and the board counted every
         * one of those points towards being cut. A third of that block was
         * outside the wood, so the meter stopped at 61% and the print could
         * not be finished at all.
         *
         * And a flat colour's outline IS the edge of the block: the Plum
         * Estate's red wash is `M0 0 H360 V240 H0 Z`, three of whose four
         * sides are the border of the plank. Nobody can cut those. The plank
         * is drawn with rounded corners and `overflow: hidden`, so a finger
         * put down on the corner does not even land on it — which is exactly
         * what happened: the pointer went down outside the rounded corner,
         * the drag never started, and the board sat at 0% with no error
         * anywhere. What is cut is the line on the FACE of the block. */
        if (w && (pt.x < edge || pt.y < edge || pt.x > w - edge || pt.y > h - edge)) continue;
        /* WHERE THE WALK JUMPED, IT IS NOT A LINE.
         *
         * getPointAtLength walks every subpath in one sequence, so between
         * the end of one shape and the start of the next it steps straight
         * across open board — and dropping the points above leaves the same
         * kind of jump behind. Neither is something the knife can follow, so
         * the segment is flagged and the measurements skip it; otherwise the
         * guide has a phantom line down the middle of it. */
        var last = out[out.length - 1];
        var jump = last && Math.hypot(pt.x - last.x, pt.y - last.y) > step * 2.5;
        out.push({ x: pt.x, y: pt.y, cut: false, gap: !!jump });
      }
    } catch (e) { out = []; }
    scratch.removeChild(el);
    return out;
  }

  /**
   * EVERY BOARD IS CARVED, ONE AFTER THE OTHER.
   *
   * It used to carve ONE — the key block, or the board `carve:` named — and
   * then hand her the other six, or sixteen, already cut. That is the largest
   * untrue thing the game said about printing, and Barb found it in her first
   * minute of play: "I only carved out one block. Player needs to carve out
   * all blocks."
   *
   * So the carving stage is now a sequence. Each board gets the knife and
   * then the chisel, and the press does not open until the last one is off
   * the bench. The nicks are kept per board, because a chip in the outline
   * board does not put a gap in the sky.
   */
  function startCarve() {
    var key = S.print;
    S.cut = {
      boards: boardsOf(key),
      at: 0,
      nicks: [],           // one list per board, in the same order
    };
    S.press = null;        // a new block has no press behind it
    carveBoard(0);
  }

  /* The blocks on one board, as a single outline to follow. */
  function boardTarget(key, i) {
    var p = printOf(key);
    var board = S.cut.boards[i];
    if (!board) return null;
    /* THE KEY BLOCK IS CUT WITH ITS OWN BOARD. It is a line rather than an
       area and it is what the picture's edges are, so it goes on the plank of
       the board it belongs to rather than being skipped. */
    var d = board.blocks.map(function (bi) { return dJoin(p.blocks[bi].d); }).join(' ');
    if (p.keyBlock && i === S.cut.boards.length - 1) d += ' ' + dJoin(p.keyBlock.d);
    return {
      d: d, ink: board.ink, name: board.name, key: false,
      pieces: (d.match(/M/g) || []).length,
    };
  }

  function carveBoard(i) {
    var key = S.print;
    var p = printOf(key);
    var mm = perMm(key);
    var target = boardTarget(key, i);
    S.cut.at = i;
    /* WHICH BOARDS HAVE A LINE TO FOLLOW, AND WHICH ARE ONLY WASTE.
     *
     * Every board is carved. Two kinds of board have no line in them, and
     * asking for the knife on either is asking for busywork:
     *
     *   STIPPLE. Hundreds of separate little marks — Red Fuji's forest is
     *   777, the moss on Kajikazawa's rock is 1,030. A carver punched those,
     *   one tap each, and traced none of them. See `texturePieces`.
     *
     *   A FLAT COLOUR THAT RUNS OFF THE PAPER. Red Fuji's blue sky is
     *   literally `M0 0 H520 V356 H0 Z` — the plank itself — and its red
     *   slope and its forest start at x = -12, outside the block. Their
     *   outline is the edge of the wood, so "follow the line" means running
     *   a finger round the border of the plank.
     *
     * The second one is measured rather than declared: sample the outline,
     * drop everything on or past the edge (samplePath already does), and ask
     * how much line is left standing in the middle of the block. */
    var edge = 3 * S.data.carve.tolerance * mm;
    var pts = samplePath(target.d, S.data.carve.tolerance * mm, p.w, p.h, edge);
    /* what is left once the block's own edge is taken out of it */
    var inside = pts.length;
    var texture = target.pieces > (S.data.carve.texturePieces || 60);
    var bleeds = inside < 40;
    S.carve = {
      target: target,
      texture: texture,
      bleeds: bleeds && !texture,
      inside: inside,
      pts: pts,
      pass: (texture || bleeds) ? 2 : 1,
      nicks: [],
      cleared: {},       // which cells of the waste have gone
      cells: 0,
      fresh: [],         // what this stroke marked, so the drag can draw only that
      down: false,
      w: p.w, h: p.h,
    };
    S.carve.need = S.carve.pts.length;
    S.view = 'carve';
    paint();
  }

  /* The knife and the chisel are done with this board; on to the next, or to
     the press if that was the last of them. */
  function nextPlank() {
    S.cut.nicks[S.cut.at] = S.carve.nicks;
    if (S.cut.at + 1 < S.cut.boards.length) { carveBoard(S.cut.at + 1); return; }
    startPress();
  }

  /* Every nick out of every board, which is what the worker scores the line
     by. Kept flat because that is the shape `judge` reads, and tagged with
     the board so a gap lands in the right outline. */
  function allNicks() {
    var out = [];
    if (!S.cut) return out;
    for (var i = 0; i < S.cut.nicks.length; i++) {
      var list = S.cut.nicks[i] || [];
      for (var j = 0; j < list.length; j++) {
        out.push({ t: list[j].t, d: list[j].d, board: i });
      }
    }
    return out;
  }

  /**
   * A STROKE OF THE KNIFE. Everything in millimetres.
   *
   * ON the line and it cuts; OFF it and it nicks, and the nick is recorded
   * where on the line it happened so the print can break there. A nick is not
   * a score — it is damage, and it is in her print for as long as she owns it.
   */
  /**
   * A STROKE OF THE KNIFE, WALKED AT THE SAME RESOLUTION EVERY TIME.
   *
   * A finger reports positions at whatever rate the device feels like, and a
   * fast drag jumps thirty millimetres between two of them. Two wrong ways to
   * deal with that, both of which this file has been:
   *
   *   Judge only the reported positions, and the line is left uncut in the
   *   gaps — the harness saw that as "following the line cuts it — 12%".
   *
   *   Judge the whole segment between them at once, and the long segment of
   *   a fast phone sweeps a wide swath and is counted as one touch of the
   *   line, while the short segments of a slow one are counted separately.
   *   The same wander then costs one nick on one phone and five on another,
   *   which the harness caught by drawing one corridor twice.
   *
   * So the stroke is walked in steps of half a tolerance, and every step is
   * judged on its own. What it costs depends on the line she drew and on
   * nothing else about the machine she drew it on.
   */
  function knife(ax, ay, bx, by) {
    var mm = perMm(S.print);
    var tol = S.data.carve.tolerance * mm;
    var span = Math.hypot(bx - ax, by - ay);
    /* the cap is for a pointer that has been away for a second, not for play */
    var steps = Math.max(1, Math.min(400, Math.ceil(span / (tol / 2))));
    for (var s = 1; s <= steps; s++) {
      var f = s / steps;
      cutAt(ax + (bx - ax) * f, ay + (by - ay) * f, tol, mm);
    }
  }

  /**
   * HOW FAR THIS POINT IS FROM THE LINE — from the line, not from the dots on
   * it.
   *
   * Measuring to the nearest SAMPLE says "off the line" for a point sitting
   * exactly on it between two dots, and since the knife is on the line again
   * at the next dot, a perfect trace scores a nick every few millimetres.
   * That is how it read before samplePath was spaced by the tolerance: two
   * and a half thousand nicks for following the guide exactly.
   *
   * IT NO LONGER CARRIES THAT ON ITS OWN. Sampling at half a tolerance keeps
   * the dots close enough that measuring to them would now be near enough,
   * and taking this out does not fail the harness. It still matters for a
   * print long enough to hit the 3000-point cap, where the spacing grows
   * again — which is exactly the case nothing has yet been drawn for.
   */
  function toLine(x, y, tol) {
    var c = S.carve;
    var near = -1, best = Infinity;
    var on = null;
    for (var i = 0; i < c.pts.length; i++) {
      var a = c.pts[i];
      var b = (i + 1 < c.pts.length && !c.pts[i + 1].gap) ? c.pts[i + 1] : null;
      var d;
      if (!b) {
        d = Math.hypot(a.x - x, a.y - y);
      } else {
        var vx = b.x - a.x, vy = b.y - a.y;
        var len2 = vx * vx + vy * vy;
        var t = len2 ? ((x - a.x) * vx + (y - a.y) * vy) / len2 : 0;
        t = t < 0 ? 0 : (t > 1 ? 1 : t);
        d = Math.hypot(x - (a.x + vx * t), y - (a.y + vy * t));
      }
      if (d < best) { best = d; near = i; }
      /* EVERY LINE UNDER THE KNIFE, NOT THE NEAREST ONE.
       *
       * The Plum Estate's limbs overlap — that is why its block is drawn as
       * separate paths in the first place — so in the overlaps two outlines
       * lie within a knife's width of each other, and a cut in the wood does
       * not choose between them.
       *
       * IT IS NOT WHAT FIXED THE PLUM ESTATE, though it was written for
       * that. The board stopped at 61% because a third of its line ran off
       * the bottom of the plank (see samplePath), and dropping the knife's
       * removal of the choice bought only three points of it. Taking this
       * out again does not fail the harness. It stays because it is true of
       * a knife and cheap, not because anything is standing on it. */
      if (d <= tol) { (on || (on = [])).push(i); }
    }
    return { dist: best, near: near, on: on };
  }

  function cutAt(x, y, tol, mm) {
    var c = S.carve;
    var hit = toLine(x, y, tol);
    var near = hit.near;
    if (near < 0) return;
    var dist = hit.dist;
    if (dist <= tol) {
      c.off = false;
      var list = hit.on || [near];
      for (var k = 0; k < list.length; k++) {
        /* both ends of each segment the knife is sitting on */
        for (var j = list[k]; j <= Math.min(c.pts.length - 1, list[k] + 1); j++) {
          if (!c.pts[j].cut) c.fresh.push(j);
          c.pts[j].cut = true;
        }
      }
      return;
    }
    if (dist < tol * 6) {
      /* A NICK IS LEAVING THE LINE, NOT BEING OFF IT.
       *
       * The first rule here recorded a nick per move event that was off the
       * line, coalesced only by distance ALONG the line — so one long wander
       * beside the line was thirty-six nicks, which is past `ruinedAt`. A
       * shaky hand would have destroyed a board in half a second.
       *
       * A carver's knife going astray and coming back is ONE chip out of the
       * line. So the nick is taken on the way out, and no more are taken
       * until the knife is back on the line. */
      if (!c.off) {
        c.off = true;
        c.nicks.push({
          t: near / Math.max(1, c.pts.length - 1),
          d: Math.max(-4, Math.min(4, (dist - tol) / mm)),
        });
      }
    }
  }

  /* Pass two: clearing the waste. A coarse grid, because what is being
     measured is "has she been over most of it" and not a shape. */
  var CELL = 14;
  function chisel(ax, ay, bx, by) {
    /* the same gap the knife had: sweep the stroke, not its end */
    var steps = Math.ceil(Math.hypot(bx - ax, by - ay) / (CELL / 2));
    for (var s = 0; s <= steps; s++) {
      var f = steps ? s / steps : 0;
      chiselAt(ax + (bx - ax) * f, ay + (by - ay) * f);
    }
  }
  /* The grid of cells that is actually ON the block. */
  function gridOf(c) {
    return { cols: Math.ceil(c.w / CELL), rows: Math.ceil(c.h / CELL) };
  }

  function chiselAt(x, y) {
    var c = S.carve;
    var g = gridOf(c);
    var cx = Math.floor(x / CELL), cy = Math.floor(y / CELL);
    /* A CHISEL IS WIDER THAN A KNIFE. Two cells either way rather than one:
       clearing the waste is the satisfying half of carving and it should feel
       like sweeping, not like colouring in. */
    for (var a = -2; a <= 2; a++) {
      for (var b = -2; b <= 2; b++) {
        var gx = cx + a, gy = cy + b;
        /* AND ONLY CELLS THAT ARE ON THE BLOCK.
         *
         * There was no bounds check here, so a sweep along any edge banked
         * five rows of cells that are off the plank — counted towards the
         * meter, drawn outside the svg where nothing shows them, and never
         * clearable by anybody because they do not exist. The meter read
         * 100% with about half the block actually swept, which is what Barb
         * saw. A cell nobody can see is not waste anybody removed. */
        if (gx < 0 || gy < 0 || gx >= g.cols || gy >= g.rows) continue;
        var k = gx + ':' + gy;
        if (!c.cleared[k]) { c.cleared[k] = true; c.cells++; c.fresh.push(k); }
      }
    }
  }
  function cutSoFar() {
    var c = S.carve, n = 0;
    for (var i = 0; i < c.pts.length; i++) if (c.pts[i].cut) n++;
    return c.pts.length ? n / c.pts.length : 1;
  }
  /* HOW MUCH OF THE WASTE HAS GONE.
   *
   * The bar is the config's `clearedEnough`, and it is a fraction of the
   * board rather than a fraction of a fraction — the first version wanted
   * 88% of 55% of the plank and the button never came on, because clearing
   * five hundred cells with a chisel the size of a fingertip is not a thing
   * anybody would sit through. Clearing is the forgiving half of this stage;
   * the knife is the half that is meant to be hard. */
  function clearedSoFar() {
    var c = S.carve;
    var g = gridOf(c);
    /* THE SAME GRID THE CHISEL STAMPS INTO. This used to divide by
       `(c.w / CELL) * (c.h / CELL)` — the unrounded count — while the chisel
       worked in whole cells, so the two disagreed about how many there were
       before the bounds bug above was even reached. */
    var want = Math.ceil(g.cols * g.rows * (S.data.carve.clearedEnough || 0.86));
    return Math.min(1, c.cells / want);
  }

  /* ================================================================== *
   * STAGE TWO — THE PRESS
   * ================================================================== */

  function startPress() {
    var key = S.print;
    var boards = boardsOf(key);
    S.press = {
      boards: boards,
      at: 0,
      /* what each board came out as; this IS the recipe */
      rec: boards.map(function () { return { dx: 0, dy: 0, ink: 0, wipe: 0 }; }),
      phase: boards.length && boards[0].wiped ? 'wipe' : 'seat',
      sheet: { x: 0, y: 0 },     // where she has dragged it, in mm
      rubbed: {},
      rubs: 0,
      down: false,
    };
    S.view = 'press';
    paint();
  }

  function board() { return S.press.boards[S.press.at]; }
  function rec() { return S.press.rec[S.press.at]; }

  /* SEATING. Inside the notch it clicks home; outside it, it prints where it
     was put. The kentō is a physical stop and this is what makes it one. */
  function seat() {
    var pr = S.press;
    var off = Math.hypot(pr.sheet.x, pr.sheet.y);
    if (off <= S.data.kento.snap) { pr.sheet.x = 0; pr.sheet.y = 0; }
    rec().dx = Math.round(pr.sheet.x * 10) / 10;
    rec().dy = Math.round(pr.sheet.y * 10) / 10;
    pr.phase = 'rub';
    pr.rubbed = {}; pr.rubs = 0;
    paint();
  }

  var RUB = 10;   // mm per cell of the rubbing grid
  function baren(x, y) {
    var pr = S.press;
    var r = Math.ceil(S.data.baren.radius / RUB);
    var cx = Math.floor(x / RUB), cy = Math.floor(y / RUB);
    for (var a = -r; a <= r; a++) {
      for (var b = -r; b <= r; b++) {
        if (a * a + b * b > r * r) continue;
        var k = (cx + a) + ':' + (cy + b);
        if (!pr.rubbed[k]) { pr.rubbed[k] = true; pr.rubs++; }
      }
    }
    rec().ink = Math.min(1, pr.rubs / wantRubs());
  }
  function wantRubs() {
    return Math.ceil((SHEET.w / RUB) * (SHEET.h / RUB) * 0.82);
  }

  /** The next board, or the end of the pull. */
  function nextBoard() {
    var pr = S.press;
    pr.at++;
    if (pr.at >= pr.boards.length) return finish();
    pr.sheet = { x: 0, y: 0 };
    pr.rubbed = {}; pr.rubs = 0;
    pr.phase = pr.boards[pr.at].wiped ? 'wipe' : 'seat';
    paint();
  }

  async function finish() {
    if (S.busy) return;
    S.busy = true; paint();
    try {
      var out = await api('/hanga/pull', {
        print: S.print,
        recipe: { boards: S.press.rec, nicks: allNicks() },
      });
      S.done = out;
      S.view = 'done';
      S.said = null;
      /* THE BLOCK AND THE PRESS ARE SPENT. They were left standing, so the
         next sheet she picked up resumed into the PREVIOUS print's press —
         its boards, its rubbing, its offsets — with a new print's name at
         the top. */
      S.press = null;
      S.carve = null;
      S.cut = null;
      if (out.companion && window.Arcade && window.Arcade.companion) {
        window.Arcade.companion(out.companion);
      }
      await load();
    } catch (err) {
      S.said = { text: human(err), kind: 'bad' };
      S.view = 'shelf';
    } finally { S.busy = false; paint(); }
  }

  /* A WAY BACK TO THE SHELF FROM THE MIDDLE OF A PRINT.
   *
   * There was none: taking a sheet dropped you into the carve view and the
   * only button on it was the one you could not press yet. A child who
   * wanted to look at the other prints, or who took the wrong sheet, had
   * nowhere to go but the browser's back button — and the worker has always
   * supported coming back to an open sheet, so the page was the only thing
   * standing in the way. The work stays where it is; the same card on the
   * shelf says Carry on rather than Take a sheet. */
  var BENCH = '<button class="hg-ghost" data-bench>Leave it on the bench</button>';

  /* ---- drawing -------------------------------------------------------- */

  function sheetSvg(key, recipe, opts) {
    var p = printOf(key);
    if (!p) return '';
    var o = opts || {};
    var upTo = o.upTo === undefined ? 999 : o.upTo;
    var mm = perMm(key);
    var boards = boardsOf(key);
    /* HER OWN COPY OF THE DEFS. A shelf is five prints on one page and a
       finished print may be drawn beside the one on the press, so the clip
       paths and gradients are prefixed per drawing — two elements sharing an
       id means the first one wins for every url(#...) after it. */
    var pre = Art.idsOf(p) + (o.tag ? o.tag + '-' : '');
    var s = Art.defsSvg(p, pre);
    /* the paper first, if this print has a sheet block */
    var all = Art.boardsOf(p);
    for (var i = 0; i < all.length; i++) {
      if (!all[i].sheet) break;
      s += all[i].blocks.map(function (bi) { return Art.blockSvg(p, p.blocks[bi], pre); }).join('');
    }
    /* THE BOARD SHE IS ABOUT TO PRINT, SHOWING THROUGH THE PAPER.
     *
     * While she is seating the sheet the board under it is inked and waiting,
     * and the first version of this drew only the boards already pulled — so
     * board one of seven was a blank sheet of paper and a notch, with nothing
     * to tell her what she was lining up or what the next colour would be.
     * The ghost is what is actually under her hands. */
    var ghostAt = o.ghost ? upTo - 1 : -1;
    for (var b = 0; b < boards.length && b < upTo; b++) {
      var r = (recipe && recipe[b]) || { dx: 0, dy: 0, ink: 1 };
      var a = Math.max(0.1, Math.min(1, r.ink === undefined ? 1 : r.ink));
      /* 0.45, because the translucent paper lies over the top of it: the
         two together come out at about a quarter, which is as faint as a
         board can be and still tell her what colour is coming. */
      if (b === ghostAt) a = 0.45;
      var paths = boards[b].blocks.map(function (bi) { return Art.blockSvg(p, p.blocks[bi], pre); }).join('');
      /* NAMED, so a rub can raise this one board's ink without the page
         being rebuilt under the finger doing the rubbing. */
      s += '<g data-board="' + b + '" transform="translate(' + ((r.dx || 0) * mm).toFixed(2) + ' '
        + ((r.dy || 0) * mm).toFixed(2) + ')" opacity="' + a.toFixed(2) + '">' + paths + '</g>';
    }
    if (p.keyBlock && upTo >= boards.length) {
      s += keyBlockSvg(p, (o.nicks || []), pre);
    }
    return '<svg class="hg__sheet" viewBox="0 0 ' + p.w + ' ' + p.h + '" role="img"'
      + ' aria-label="' + esc(p.title) + '">' + s + '</svg>';
  }

  /**
   * THE KEY BLOCK, WITH HER NICKS IN IT. A nick is a gap in the line, so the
   * line is drawn as a dash pattern that is solid everywhere except where she
   * wandered — which is exactly what a chipped block prints like.
   */
  function keyBlockSvg(p, nicks, pre) {
    var gaps = (nicks || []).slice(0, 40);
    if (!gaps.length) {
      return '<path d="' + dJoin(p.keyBlock.d) + '" fill="none" stroke="' + Art.inkOf(p.keyBlock.ink, pre)
        + '" stroke-width="' + p.keyBlock.stroke + '" stroke-linecap="round"'
        + ' stroke-linejoin="round"/>';
    }
    /* one mask, with a hole at each nick */
    var id = (pre || '') + 'hgnick';
    var holes = gaps.map(function (n) {
      return '<circle cx="0" cy="0" r="0" data-t="' + n.t + '"/>';
    }).join('');
    /* The holes are placed by the browser from the same path, so a nick lands
       where the knife actually slipped rather than where arithmetic here
       thinks it did. */
    return '<mask id="' + id + '"><rect width="' + p.w + '" height="' + p.h + '" fill="#fff"/>'
      + holes + '</mask>'
      + '<path d="' + dJoin(p.keyBlock.d) + '" fill="none" stroke="' + Art.inkOf(p.keyBlock.ink, pre)
      + '" stroke-width="' + p.keyBlock.stroke + '" stroke-linecap="round"'
      + ' stroke-linejoin="round" mask="url(#' + id + ')" data-nickpath/>';
  }

  /* The nick holes are positioned after the svg is in the document, because
     that is when the path can be measured. */
  function placeNicks(scope, key, nicks) {
    var path = scope.querySelector('[data-nickpath]');
    var mask = scope.querySelector('mask');
    if (!path || !mask || !nicks || !nicks.length) return;
    var p = printOf(key);
    var len = 0;
    try { len = path.getTotalLength(); } catch (e) { return; }
    var holes = mask.querySelectorAll('circle');
    var r = Math.max(2.5, (S.data.carve.nick || 3.2) * perMm(key) / 2);
    for (var i = 0; i < holes.length; i++) {
      var t = Number(holes[i].getAttribute('data-t')) || 0;
      var pt = path.getPointAtLength(len * t);
      holes[i].setAttribute('cx', pt.x);
      holes[i].setAttribute('cy', pt.y);
      holes[i].setAttribute('r', r);
      holes[i].setAttribute('fill', '#000');
    }
  }

  /* ---- the views ------------------------------------------------------ */

  function shelf() {
    var d = S.data;
    var rows = (d.prints || []).map(function (row) {
      var p = printOf(row.key);
      if (!p) return '';
      var a = Art.ARTISTS[p.artist] || {};
      var afford = d.guest || d.points >= row.cost;
      var onBench = !!(d.open && d.open.print === row.key);
      return '<li class="hg-card">'
        + '<div class="hg-card__pic">' + sheetSvg(row.key, null, {}) + '</div>'
        + '<div class="hg-card__say">'
        + '<h3>' + esc(p.title) + (p.also ? ' <em>(' + esc(p.also) + ')</em>' : '') + '</h3>'
        + '<p class="hg-jp">' + esc(p.jp) + '</p>'
        + '<p class="hg-by">' + esc(a.name) + ' &middot; ' + esc(p.date)
        + ' &middot; ' + esc(p.series) + '</p>'
        + '<p class="hg-fact">' + esc(p.fact) + '</p>'
        + '<p class="hg-links">'
        + '<a href="' + esc(p.museum) + '" target="_blank" rel="noopener">'
        + esc(p.museumName) + '</a> &middot; '
        + '<a href="' + esc(p.wiki) + '" target="_blank" rel="noopener">Wikipedia</a> &middot; '
        + '<a href="' + esc(a.wiki) + '" target="_blank" rel="noopener">' + esc(a.name) + '</a>'
        + '</p>'
        + '<p class="hg-boards">' + row.boards + ' boards'
        + (row.keyBlock ? ' and a key block' : ', and no key block')
        + (row.wiped.length ? ' &middot; ' + row.wiped.length + ' wiped' : '') + '</p>'
        + (row.pulls ? '<p class="hg-best">Pulled ' + row.pulls + '&times;, best ' + row.best + '</p>' : '')
        + '</div>'
        /* THE CARD SAYS WHICH IT IS. A sheet already on the bench costs
           nothing to go back to, and offering "Take a sheet — 31" for work
           she has already paid for reads as being charged twice. */
        + '<div class="hg-card__go">'
        + '<button class="hg-go" data-take="' + esc(row.key) + '"'
        + (d.guest || S.busy || (!afford && !onBench) ? ' disabled' : '') + '>'
        + '<span class="hg-go__what">' + (onBench ? 'Carry on with it' : 'Take a sheet') + '</span>'
        + (onBench ? '' : '<span class="hg-go__cost">' + money(row.cost) + '</span>') + '</button>'
        + (afford || onBench ? '' : '<span class="hg-short">Not enough yet</span>')
        + '</div></li>';
    }).join('');

    var mine = (d.mine || []).map(function (pr) {
      var p = printOf(pr.print);
      return '<li class="hg-mine">'
        + '<div class="hg-mine__pic">'
        + sheetSvg(pr.print, pr.recipe && pr.recipe.boards, { nicks: pr.recipe && pr.recipe.nicks })
        + '</div>'
        + '<p class="hg-mine__name">' + esc(p ? p.title : pr.print) + '</p>'
        + '<p class="hg-mine__score">' + pr.score + ' out of 100</p>'
        + '<button class="hg-bin" data-bin="' + pr.at + '">Bin it</button>'
        + '</li>';
    }).join('');

    return '<section class="hg">'
      + '<div class="hg__top"><h2>Woodblock Printing <span class="jp">木版画</span></h2>'
      + (d.guest ? '' : '<span class="hg__purse">' + money(d.points) + ' points</span>')
      + '</div>'
      + '<p class="hg-lede">A print is not one picture, it is a stack of them &mdash; '
      + 'one board for every colour, pressed onto the same sheet in turn. Cut the '
      + 'line block, then pull the colours over it, and the paper has to land in '
      + '<b>exactly</b> the same place every time.</p>'
      + (d.open ? '<p class="hg-open">There is a sheet of '
        + esc((printOf(d.open.print) || {}).title || d.open.print)
        + ' on the press. <button class="hg-resume" data-take="' + esc(d.open.print)
        + '">Go back to it</button></p>' : '')
      + (S.said ? '<p class="hg-said hg-said--' + S.said.kind + '">' + esc(S.said.text) + '</p>' : '')
      + '<ul class="hg-cards">' + rows + '</ul>'
      + (mine ? '<h3 class="hg-h">Prints you have pulled</h3><ul class="hg-shelf">' + mine + '</ul>'
        : '')
      + '</section>';
  }

  /* What the meter says, in one place, because the drag updates it sixty
     times a second and the full render writes it once — and two copies of
     this sentence is two chances for the number on the page to disagree with
     the number the button is reading. */
  function carveSays() {
    var c = S.carve;
    if (c.pass !== 1) return Math.round(clearedSoFar() * 100) + '% cleared';
    var cut = Math.round(cutSoFar() * 100);
    return cut + '% cut' + (c.nicks.length
      ? ' &middot; ' + c.nicks.length + ' nick' + (c.nicks.length === 1 ? '' : 's')
      : ' &middot; no nicks yet');
  }
  function carveReady() {
    var c = S.carve;
    var f = c.pass === 1 ? cutSoFar() : clearedSoFar();
    return f >= (c.pass === 1 ? 0.88 : 1);
  }
  function markSvg(i) {
    var c = S.carve;
    if (c.pass === 1) {
      var q = c.pts[i];
      return '<circle cx="' + q.x.toFixed(1) + '" cy="' + q.y.toFixed(1) + '" r="'
        + (1.8 * perMm(S.print)).toFixed(1) + '" fill="#6B4B2A"/>';
    }
    var xy = String(i).split(':');
    return '<rect x="' + (xy[0] * CELL) + '" y="' + (xy[1] * CELL) + '" width="' + CELL
      + '" height="' + CELL + '" fill="#E9DCC4"/>';
  }

  /**
   * THE DRAG DOES NOT REDRAW THE PAGE.
   *
   * It used to: every pointermove ran the whole `paint()`, which rebuilt
   * root.innerHTML, which rebuilt the plank svg, which emitted one <circle>
   * for every cut sample — up to nine hundred of them — and then threw the
   * lot away on the next event. A finger reports about sixty events a second.
   * The harness found it by timing out: three hundred moves along one line
   * took longer than two minutes, and on a phone it would simply not have
   * worked.
   *
   * So a stroke appends the marks it actually made, and touches four other
   * things: the meter, the sentence under it, and the button. */
  function repaintCarve() {
    var c = S.carve;
    var g = root.querySelector('[data-ink]');
    if (!g) { paint(); return; }
    var add = '';
    for (var i = 0; i < c.fresh.length; i++) add += markSvg(c.fresh[i]);
    c.fresh.length = 0;
    if (add) g.insertAdjacentHTML('beforeend', add);

    var bar = root.querySelector('.hg-meter span');
    if (bar) bar.style.width = Math.round((c.pass === 1 ? cutSoFar() : clearedSoFar()) * 100) + '%';
    var says = root.querySelector('.hg-quiet');
    if (says) says.innerHTML = carveSays();
    var go = root.querySelector('[data-carve-on]');
    if (go) go.disabled = !carveReady();
  }

  function carveView() {
    var c = S.carve;
    var p = printOf(S.print);
    var line = '<path d="' + c.target.d + '" fill="none" stroke="'
      + (c.pass === 1 ? '#C9B79A' : '#6B4B2A') + '" stroke-width="'
      + (3.2 * perMm(S.print)) + '" stroke-linecap="round" stroke-linejoin="round"/>';
    var marks = '<g data-ink>' + (c.pass === 1
      ? c.pts.map(function (q, i) { return q.cut ? markSvg(i) : ''; }).join('')
      : Object.keys(c.cleared).map(markSvg).join('')) + '</g>';
    /* the cut dots sit on the guide; the cleared waste sits under the line */
    var guide = c.pass === 1 ? line + marks : marks + line;
    c.fresh.length = 0;

    /* WHICH BOARD OF HOW MANY. Every board is carved, so a player who is not
       told is a player who thinks she is nearly done on her second of
       seventeen. */
    var which = S.cut
      ? 'Board ' + (S.cut.at + 1) + ' of ' + S.cut.boards.length + ' &middot; ' + esc(p.title)
      : esc(p.title);
    return '<section class="hg hg--carve">'
      + '<div class="hg__top"><h2>Cutting ' + esc(c.target.name) + '</h2>'
      + '<span class="hg__of">' + which + '</span></div>'
      + '<p class="hg-step">' + (c.pass === 1
        ? 'Drag along every line. The knife cuts where you follow it &mdash; stray '
          + 'off and you take a nick out of the line, and the nick stays in every '
          + 'print you pull from this block.'
        : (c.bleeds
          ? 'Clear the face of the block. This one is a flat colour, so all of it '
            + 'prints and all of it has to come clean.'
          : c.texture
          ? 'Punch out the waste around every mark. Drag broadly &mdash; this is '
            + 'the slow, patient part of a carver\'s day, and it is meant to feel '
            + 'like sweeping rather than drawing.'
          : 'Now clear everything that is <b>not</b> line. This part is forgiving: '
            + 'drag broadly, and the picture appears as the waste goes.')) + '</p>'
      + (c.texture ? '<p class="hg-note">This board is <b>stipple</b> &mdash; '
        + c.target.pieces + ' separate little marks. A carver cut these with a '
        + '<b>punch</b>, tapping once for each one, so there is no line to follow: '
        + 'clear the waste and the marks come up as you go.</p>'
        : c.bleeds ? '<p class="hg-note">This is a <b>flat colour</b> that runs off '
          + 'every edge of the paper, so its only outline is the edge of the block '
          + 'itself. There is nothing to follow with the knife: clear it and it is '
          + 'ready to ink.</p>' : '')
      + '<div class="hg-plank" data-plank>'
      + '<svg viewBox="0 0 ' + c.w + ' ' + c.h + '" class="hg-plank__svg">'
      + '<rect width="' + c.w + '" height="' + c.h + '" fill="#D9C39B"/>' + guide + '</svg>'
      + '</div>'
      + '<p class="hg-meter"><span style="width:'
      + Math.round((c.pass === 1 ? cutSoFar() : clearedSoFar()) * 100) + '%"></span></p>'
      + '<p class="hg-quiet">' + carveSays() + '</p>'
      + '<div class="hg-row">'
      + '<button class="hg-go" data-carve-on' + (carveReady() ? '' : ' disabled') + '>'
      + (c.pass === 1 ? 'Clear the waste'
        : (S.cut && S.cut.at + 1 < S.cut.boards.length ? 'Next board' : 'Take them to the press'))
      + '</button>'
      + BENCH
      + '</div></section>';
  }

  function pressView() {
    var pr = S.press;
    var b = board();
    var p = printOf(S.print);
    var mm = perMm(S.print);
    var done = pr.at;
    /* THE LAST BOARD HAS BEEN LIFTED and the pull is away at the worker, so
       there is no board to name and no record to read. Everything below this
       line assumed there always was one, and drawing the press while the
       fetch was in flight threw on `b.name`. */
    if (!b) {
      return '<section class="hg hg--press">'
        + '<div class="hg__top"><h2>' + esc(p.title) + '</h2></div>'
        + '<div class="hg-press">' + sheetSvg(S.print, pr.rec, {}) + '</div>'
        + '<p class="hg-quiet">Lifting the sheet off the last board&hellip;</p></section>';
    }
    var shown = pr.phase === 'rub'
      ? sheetSvg(S.print, pr.rec, { upTo: done + 1 })
      : sheetSvg(S.print, pr.rec, { upTo: done + 1, ghost: true });

    var what = pr.phase === 'wipe'
      ? 'This board is <b>wiped</b>. Drag the brush from the edge the colour '
        + 'starts at: how far you pull it is how far the colour reaches.'
      : pr.phase === 'seat'
        ? 'Drag the sheet until its corner drops into the <b>kent&#x14d;</b> &mdash; the '
          + 'notch at the bottom left. Close enough and it clicks home square.'
        : 'Now rub. Drag the <b>baren</b> over the back of the sheet; the ink takes '
          + 'where you rubbed and nowhere else.';

    var off = Math.hypot(pr.sheet.x, pr.sheet.y);
    return '<section class="hg hg--press">'
      + '<div class="hg__top"><h2>' + esc(p.title) + '</h2>'
      + '<span class="hg__of">Board ' + (pr.at + 1) + ' of ' + pr.boards.length
      + ' &middot; ' + esc(b.name) + '</span></div>'
      + '<p class="hg-step">' + what + '</p>'
      + '<div class="hg-press" data-press data-phase="' + pr.phase + '">'
      + shown
      + (pr.phase === 'seat'
        ? '<div class="hg-paper" style="transform:translate(' + (pr.sheet.x / SHEET.w * 100).toFixed(2)
          + '%,' + (pr.sheet.y / SHEET.h * 100).toFixed(2) + '%)"></div>'
          + '<span class="hg-kento hg-kento--corner"></span>'
          + '<span class="hg-kento hg-kento--edge"></span>'
        : '')
      + '</div>'
      + (pr.phase === 'seat'
        ? '<p class="hg-quiet">' + (off <= S.data.kento.snap
          ? 'Seated square.' : off.toFixed(1) + ' mm out of true.') + '</p>'
        : pr.phase === 'rub'
          ? '<p class="hg-meter"><span style="width:' + Math.round(rec().ink * 100) + '%"></span></p>'
          : '<p class="hg-quiet">Wipe set to ' + Math.round((rec().wipe + 1) * 50) + '.</p>')
      + '<div class="hg-row">'
      + (pr.phase === 'wipe'
        ? '<button class="hg-go" data-wiped>Ink it</button>'
        : pr.phase === 'seat'
          ? '<button class="hg-go" data-seat>Press it down</button>'
          : '<button class="hg-go" data-next' + (rec().ink > 0.15 ? '' : ' disabled') + '>'
            + (pr.at + 1 >= pr.boards.length ? 'Lift the last sheet' : 'Next board') + '</button>')
      + BENCH
      + '</div></section>';
  }

  function doneView() {
    var o = S.done;
    var p = printOf(o.print);
    var WHY = {
      line: 'The knife wandered. Those breaks in the outline are nicks you took out of the block.',
      register: 'A board did not seat. ' + (o.worstOffMm ? o.worstOffMm + ' mm out of true' : 'Out of true')
        + ' is the difference between a print and a near miss.',
      ink: 'Parts of it never got rubbed, so the ink did not take there.',
      wipe: 'The wipe landed in the wrong place, so the colour fades where it should not.',
    };
    return '<section class="hg hg--done">'
      + '<div class="hg__top"><h2>' + esc(p.title) + '</h2>'
      + '<span class="hg__of">' + o.score + ' out of 100</span></div>'
      + '<div class="hg-done__pic">'
      + sheetSvg(o.print, o.recipe && o.recipe.boards, { nicks: o.recipe && o.recipe.nicks })
      + '</div>'
      + (o.worst ? '<p class="hg-why">' + esc(WHY[o.worst] || '') + '</p>'
        : '<p class="hg-why">Every board square, every one rubbed. That is a good pull.</p>')
      + '<p class="hg-paid">' + money(o.pays) + ' points'
      + (o.first ? ' &mdash; and the first one of these you have finished.' : '.') + '</p>'
      + '<p class="hg-quiet">It is hanging in your room, or you can bin it. '
      + 'A printer binned most of what he pulled.</p>'
      + '<div class="hg-row">'
      + '<button class="hg-go" data-again="' + esc(o.print) + '">Pull another</button>'
      + '<button class="hg-ghost" data-bin="' + o.at + '">Bin it</button>'
      + '<button class="hg-ghost" data-shelf>Back to the prints</button>'
      + '</div></section>';
  }

  /* ---- paint and wire ------------------------------------------------- */

  function paint() {
    if (!S.data) { root.innerHTML = '<p class="hg-quiet">Opening the workshop&hellip;</p>'; return; }
    SHEET = S.data.sheet || SHEET;
    root.innerHTML = S.view === 'carve' ? carveView()
      : S.view === 'press' ? pressView()
        : S.view === 'done' ? doneView() : shelf();
    /* the nicks are placed once the svg can be measured */
    var scopes = root.querySelectorAll('.hg-done__pic, .hg-mine__pic');
    for (var i = 0; i < scopes.length; i++) {
      var owner = scopes[i].closest('[data-nicks-for]');
      placeNicks(scopes[i], S.view === 'done' ? S.done.print : (owner && owner.getAttribute('data-nicks-for')),
        S.view === 'done' ? (S.done.recipe && S.done.recipe.nicks) : null);
    }
  }

  /* One pointer handler for all three drags: which one it is depends on the
     view, and every one of them converts to millimetres at the edge.
   *
   * MEASURED ON THE DRAWING, NOT ON THE BOX AROUND IT. The plank and the
   * press both have a two-pixel border, so the svg inside them is inset by
   * two pixels on every side — and mapping a finger through the wrapper's
   * rectangle put every stroke about five units off the line. The harness
   * caught it as twelve nicks taken while tracing the guide exactly, which
   * is not a thing a player could have caused. */
  function xyIn(el, ev, wide, tall) {
    var art = el.querySelector('svg') || el;
    var r = art.getBoundingClientRect();
    return {
      x: ((ev.clientX - r.left) / r.width) * wide,
      y: ((ev.clientY - r.top) / r.height) * tall,
    };
  }

  root.addEventListener('pointerdown', function (ev) {
    var plank = ev.target.closest('[data-plank]');
    if (plank && S.carve) {
      S.carve.down = true;
      S.carve.last = null;   // a new stroke starts here, not where the last ended
      plank.setPointerCapture(ev.pointerId);
      onPlank(plank, ev);
      return;
    }
    var press = ev.target.closest('[data-press]');
    if (press && S.press) {
      S.press.down = true;
      S.press.from = xyIn(press, ev, SHEET.w, SHEET.h);
      S.press.was = { x: S.press.sheet.x, y: S.press.sheet.y };
      press.setPointerCapture(ev.pointerId);
      onPress(press, ev);
    }
  });
  root.addEventListener('pointermove', function (ev) {
    if (S.carve && S.carve.down) {
      var plank = root.querySelector('[data-plank]');
      if (plank) onPlank(plank, ev);
    }
    if (S.press && S.press.down) {
      var press = root.querySelector('[data-press]');
      if (press) onPress(press, ev);
    }
  });
  function up() {
    /* LIFTING THE KNIFE ENDS THE STRAY. Putting it down somewhere else and
       wandering again is a second chip, which is right — but the stray does
       not continue across the gap. */
    if (S.carve) { S.carve.down = false; S.carve.off = false; S.carve.last = null; }
    if (S.press) S.press.down = false;
  }
  root.addEventListener('pointerup', up);
  root.addEventListener('pointercancel', up);

  function onPlank(el, ev) {
    var c = S.carve;
    var at = xyIn(el, ev, c.w, c.h);
    var was = c.last || at;
    if (c.pass === 1) knife(was.x, was.y, at.x, at.y);
    else chisel(was.x, was.y, at.x, at.y);
    c.last = at;
    repaintCarve();
  }

  function onPress(el, ev) {
    var pr = S.press;
    /* THE LAST BOARD IS THE LAST BOARD. Once it has been pulled, `at` is past
       the end of the list and there is no record to write to — and the pull
       is away at the worker, so the press is still on the screen for as long
       as that takes. A child tapping the paper while she waits was throwing
       "Cannot set properties of undefined" on every frame of it. */
    if (!pr || S.busy || pr.at >= pr.boards.length) return;
    var at = xyIn(el, ev, SHEET.w, SHEET.h);
    if (pr.phase === 'seat') {
      var slop = S.data.kento.slop;
      pr.sheet.x = Math.max(-slop, Math.min(slop, pr.was.x + (at.x - pr.from.x)));
      pr.sheet.y = Math.max(-slop, Math.min(slop, pr.was.y + (at.y - pr.from.y)));
    } else if (pr.phase === 'rub') {
      baren(at.x, at.y);
    } else if (pr.phase === 'wipe') {
      /* HOW FAR SHE DRAGS IS HOW FAR THE COLOUR REACHES, and the board's own
         intended fade is the middle of the range — so a wipe of zero is the
         printer's own, and either side of it is her own. */
      var reach = (at.y - pr.from.y) / SHEET.h;
      rec().wipe = Math.max(-1, Math.min(1, reach * 2));
    }
    repaintPress();
  }

  /**
   * THE DRAG DOES NOT REDRAW THE PRESS.
   *
   * It used to end in `paint()`, which sets root.innerHTML — so the first
   * pointermove of a drag DESTROYED the very element the drag was captured
   * on, and the sheet could not be moved at all. Barb found it in a minute
   * of play; the browser harness never could, because playwright dispatches
   * pointer events at screen coordinates and does not care that the node
   * under them has been replaced since.
   *
   * It is the same mistake `repaintCarve` exists for, left in the half of
   * the file the harness was driving rather than feeling.
   */
  function repaintPress() {
    var pr = S.press;
    var el = root.querySelector('[data-press]');
    if (!el) { paint(); return; }

    if (pr.phase === 'seat') {
      var paper = el.querySelector('.hg-paper');
      if (paper) {
        paper.style.transform = 'translate(' + (pr.sheet.x / SHEET.w * 100).toFixed(2)
          + '%,' + (pr.sheet.y / SHEET.h * 100).toFixed(2) + '%)';
      }
      var off = Math.hypot(pr.sheet.x, pr.sheet.y);
      var says = root.querySelector('.hg-quiet');
      if (says) {
        says.textContent = off <= S.data.kento.snap
          ? 'Seated square.' : off.toFixed(1) + ' mm out of true.';
      }
      return;
    }

    if (pr.phase === 'rub') {
      /* the ink comes up on the board she is rubbing, and nowhere else */
      var g = el.querySelector('[data-board="' + pr.at + '"]');
      if (g) g.setAttribute('opacity', Math.max(0.1, Math.min(1, rec().ink)).toFixed(2));
      var bar = root.querySelector('.hg-meter span');
      if (bar) bar.style.width = Math.round(rec().ink * 100) + '%';
      var go = root.querySelector('[data-next]');
      if (go) go.disabled = !(rec().ink > 0.15);
      return;
    }

    /* the wipe is a number and a picture, and the picture is the point */
    paint();
  }

  root.addEventListener('click', async function (ev) {
    var t = ev.target.closest('[data-take],[data-carve-on],[data-seat],[data-next],'
      + '[data-wiped],[data-again],[data-shelf],[data-bin],[data-bench]');
    if (!t || S.busy) return;

    if (t.hasAttribute('data-shelf')) { S.view = 'shelf'; S.done = null; paint(); return; }

    if (t.hasAttribute('data-carve-on')) {
      if (S.carve.pass === 1) { S.carve.pass = 2; paint(); } else nextPlank();
      return;
    }
    if (t.hasAttribute('data-bench')) { S.view = 'shelf'; S.said = null; paint(); return; }
    if (t.hasAttribute('data-wiped')) { S.press.phase = 'seat'; paint(); return; }
    if (t.hasAttribute('data-seat')) { seat(); return; }
    if (t.hasAttribute('data-next')) { nextBoard(); return; }

    var take = t.getAttribute('data-take') || t.getAttribute('data-again');
    if (take) {
      /* COMING BACK TO WORK ALREADY DONE. The cut line and the boards already
         pulled live in this page, not in the worker — so going to the shelf
         and back has to return to them rather than hand her a fresh plank and
         quietly throw away ten minutes of carving. */
      if (take === S.print && (S.press || S.carve)
          && S.data && S.data.open && S.data.open.print === take) {
        S.view = S.press ? 'press' : 'carve';
        S.said = null; paint(); return;
      }
      S.busy = true; paint();
      try {
        var open = S.data && S.data.open;
        var force = !!(open && open.print !== take);
        if (force && !window.confirm('There is a sheet of '
          + ((printOf(open.print) || {}).title || open.print)
          + ' on the press. Starting a new one throws that sheet away. Carry on?')) {
          S.busy = false; paint(); return;
        }
        await api('/hanga/sheet', { print: take, force: force });
        S.print = take;
        await load({ quiet: true });
        startCarve();
      } catch (err) {
        S.said = { text: human(err), kind: 'bad' };
        S.view = 'shelf';
      } finally { S.busy = false; paint(); }
      return;
    }

    var bin = t.getAttribute('data-bin');
    if (bin) {
      S.busy = true; paint();
      try {
        await api('/hanga/bin', { at: Number(bin) });
        if (S.view === 'done') { S.view = 'shelf'; S.done = null; }
        await load();
      } catch (err) {
        S.said = { text: human(err), kind: 'bad' };
      } finally { S.busy = false; paint(); }
    }
  });

  async function load(opts) {
    try {
      S.data = await api('/hanga');
    } catch (err) {
      if (!S.data) {
        root.innerHTML = '<p class="hg-said hg-said--bad">' + esc(human(err)) + '</p>';
        return;
      }
    }
    if (!(opts && opts.quiet)) paint();
  }

  load();
}());
